<template>
  <div
    component-name="carousel-splitview-landscape"
    ref="tpl_root"
    class="sticky top-0 left-0 grid grid-cols-1"
  >
    <!-- SCROLL REFERENCE -->
    <div
      aria-hidden="true"
      class="w-full row-start-1 row-end-2 pointer-events-none col-span-full invisible"
    >
      <div
        inert
        v-for="(item, index) in items"
        class="flex items-center justify-center w-full border border-current bg-skin-base text-skin-base"
        :class="{
          'h-[120vh]':
            items.length > 2 && index >= 1 && index <= items.length - 2,
          'h-[150vh]': index === 0 || index === items.length - 1,
          'max-h-screen': items.length === 1,
        }"
        v-surfer="{
          setup: {
            rootMargin: '-50% 0px -50% 0px',
          },
          observers: {
            init: items.length > 1,
            visible: { event: true },
          },
        }"
        @surfer-visible="handleOnVisible(index)"
      >
        <div class="flex gap-10 truncate text-heading-4/none">
          <span>Slide index: {{ index }}</span>
          <span>Current Slide index: {{ currentIndex }}</span>
        </div>
      </div>
    </div>
    <!-- end SCROLL REFERENCE -->

    <!-- ACTUAL SLIDES -->
    <div class="w-full row-start-1 row-end-2 col-span-full">
      <div class="sticky top-0 left-0 w-full">
        <div
          ref="tpl_slidesWrapper"
          data-skin="black"
          class="relative top-0 left-0 grid w-full grid-cols-1 overflow-hidden"
        >
          <div
            v-for="(item, index) in items"
            class="col-span-full row-start-1 row-end-2 overflow-hidden relative top-0 left-0 grid grid-cols-2 w-full h-screen max-h-screen"
            :inert="currentIndex !== index"
            :style="`z-index: ${items.length - index};`"
          >
            <!-- LEFT BOX -->
            <section
              class="relative top-0 left-0 col-span-1 row-start-1 row-end-2 w-full h-screen max-h-screen"
              :class="{
                'bg-skin-base': item.box2.asset,
                'bg-dynamic': !item.box2.asset,
              }"
              :style="`--color-fill-dynamic: ${setLuminance(
                item.box1.asset.dominant_color
              )};`"
            >
              <media
                v-if="item.box2.asset"
                v-bind="item.box2.asset"
                :sizes="sizes"
                :loading="index > 1 || forceLazyLoading ? 'lazy' : 'eager'"
                :use-bg="false"
                class="absolute inset-0 block w-full h-full overflow-hidden opacity-80"
              />

              <div
                class="relative top-0 left-0 flex flex-col items-start justify-end w-full h-full p-3xl"
                :class="{
                  'overlay-gradient-from-bottom': item.box2.asset,
                }"
              >
                <wrapper-reveal
                  v-if="item.box2.overline"
                  :reveal="index === 0 ? 'slide-from-right' : undefined"
                >
                  <div
                    v-if="item.box2.overline"
                    class="text-overline text-skin-base"
                    v-html="item.box2.overline"
                  ></div>
                </wrapper-reveal>

                <wrapper-split-text
                  v-if="item.box2.title"
                  :reveal="index === 0 ? 'lines' : undefined"
                  :class="{
                    'mt-md': item.box2.overline,
                  }"
                >
                  <div
                    class="text-heading-4 text-skin-base"
                    v-html="item.box2.title"
                  ></div>
                </wrapper-split-text>

                <wrapper-reveal
                  v-if="item.box2.text"
                  :reveal="index === 0 ? 'slide-from-bottom' : undefined"
                  class="w-10/12"
                  :class="{
                    'mt-lg': item.box2.overline || item.box2.title,
                  }"
                >
                  <div
                    class="w-full line-clamp-4 text-body-big text-skin-base"
                    v-html="item.box2.text"
                  ></div>
                </wrapper-reveal>

                <wrapper-reveal
                  v-if="item.box2.button?.title && item.box2.button?.action"
                  :class="{
                    'mt-2xl':
                      item.box2.overline || item.box2.title || item.box2.text,
                  }"
                  :reveal="index === 0 ? 'slide-from-right' : undefined"
                >
                  <btn-pill
                    icon="arrow-long-right"
                    :filled="true"
                    :title="item.box2.button.title"
                    :action="item.box2.button.action"
                  />
                </wrapper-reveal>
              </div>
            </section>
            <!-- end LEFT BOX -->

            <!-- RIGHT BOX -->
            <section
              class="overflow-hidden col-span-1 row-start-1 row-end-2 w-full h-screen max-h-screen bg-skin-base"
            >
              <media
                class="w-full h-full overflow-hidden opacity-80"
                v-bind="item.box1.asset"
                :sizes="sizes"
                :loading="index > 1 || forceLazyLoading ? 'lazy' : 'eager'"
                :use-bg="false"
                :reveal="index === 0 ? 'slide-from-up' : undefined"
              />
            </section>
            <!-- end RIGHT BOX -->
          </div>

          <!-- TITLES -->
          <client-only>
            <section
              ref="tpl_titles"
              inert
              class="flex items-center justify-center w-full h-full max-h-full row-start-1 row-end-2 col-span-full text-skin-base"
              :style="`z-index: ${items.length};`"
            >
              <div
                class="relative top-0 left-0 w-full py-4 overflow-hidden text-center text-display-hero/none"
              >
                <div
                  class="invisible line-clamp-2"
                  tabindex="-1"
                  inert
                  aria-hidden="true"
                  v-html="'Placeholder<br>Placeholder'"
                ></div>

                <swiper-container
                  init="false"
                  class="absolute inset-0 w-full h-full"
                  events-prefix="swiper-"
                >
                  <swiper-slide
                    v-for="(item, index) in items"
                    class="flex items-center justify-center"
                  >
                    <wrapper-reveal
                      :reveal="index === 0 ? 'slide-from-down' : undefined"
                    >
                      <div
                        v-html="item.title"
                        class="!leading-none line-clamp-2"
                      ></div>
                    </wrapper-reveal>
                  </swiper-slide>
                </swiper-container>
              </div>
            </section>
          </client-only>
          <!-- end TITLES -->
        </div>
      </div>
    </div>
    <!-- end ACTUAL SLIDES -->
  </div>
</template>

<script setup>
const props = defineProps({
  items: {
    type: Array,
    required: true,
  },
  sizes: {
    type: Object,
    required: true,
  },
  forceLazyLoading: {
    type: Boolean,
    required: false,
  },
});
const { setLuminance } = useColors();
const tpl_root = templateRef("tpl_root");
const tpl_slidesWrapper = templateRef("tpl_slidesWrapper");
const tpl_titles = templateRef("tpl_titles");
const currentIndex = ref();

let tl;
let swiperTitlesEl;
let nativeScrollObserver;

function handleOnVisible(index) {
  if (currentIndex.value === undefined && tl)
    gsap.set(tl, { progress: (1 / props.items.length) * index });
  currentIndex.value = index;
}

useSafeMountedEl([tpl_root, tpl_slidesWrapper, tpl_titles], () => {
  const $slides = tpl_slidesWrapper.value.querySelectorAll(":scope > div");
  swiperTitlesEl = tpl_titles.value.querySelector("swiper-container");

  tl = gsap.timeline({ paused: true });

  for (let i = 0, len = $slides.length; i < len; i++) {
    tl.to($slides[i].querySelectorAll(":scope > section"), {
      duration: 1,
      yPercent: i === $slides.length - 1 ? 0 : gsap.utils.wrap([-100, 100]),
      ease: "expo.inOut",
    });

    tl.to(
      $slides[i].querySelectorAll("img"),
      {
        duration: 1.2,
        scale: 1,
        ease: "none",
      },
      "<"
    );
  }

  Object.assign(swiperTitlesEl, {
    grabCursor: false,
    speed: 1200,
    slidesPerView: 1,
    spaceBetween: 300,
    direction: "vertical",
    on: {
      init() {
        ScrollTrigger.refresh(true);
      },
    },
  });

  swiperTitlesEl.initialize();

  nativeScrollObserver = Observer.create({
    target: window, // can be any element (selector text is fine)
    type: "wheel,touch,scroll,pointer", // comma-delimited list of what to listen for
    preventDefault: true, // cancels the native event for all listeners
  });
  nativeScrollObserver.disable();

  // get the bounding rect of tpl_root relative to the viewport
  const rect = tpl_root.value.getBoundingClientRect();
  if (rect.bottom < 0) {
    currentIndex.value = props.items.length - 1;
  }

  watch(currentIndex, (val) => {
    nativeScrollObserver.enable();
    gsap.to(tl, {
      progress: (1 / props.items.length) * val,
      duration: 2,
      ease: "sine.out",
      onComplete() {
        if (nativeScrollObserver) {
          nativeScrollObserver.disable();
        }
      },
    });
    if (swiperTitlesEl) swiperTitlesEl.swiper.slideTo(currentIndex.value);
  });
});

onBeforeUnmount(() => {
  swiperTitlesEl?.swiper?.destroy();
  swiperTitlesEl = null;
  if (nativeScrollObserver) {
    nativeScrollObserver.disable();
    nativeScrollObserver = null;
  }
});
</script>
